/**
 * GoLiveOverlay — the full-screen confirmation a beta tester sees the next time
 * they open Vault after the owner moves testers to live. It cannot be closed:
 * the only ways past it are "Continue with Vault Pro" (answer accepted → the
 * paywall, where the plan is chosen and paid; nothing is charged here) or
 * "No thanks" (a second confirm, then access ends with no charge — export and
 * delete stay available under POPIA). The server blocks every budget call with
 * `golive_confirm_required` until one is chosen, so this is the only screen that
 * works while `me.golive_confirm` is true.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';

export default function GoLiveOverlay() {
  const { reload, logout } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState(null);
  const [step, setStep] = useState('ask');     // ask | confirm-decline
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Live prices, so the overlay never quotes a figure Paystack won't charge.
  useEffect(() => {
    api('/billing/status/').then(setPlans).catch(() => setPlans(null));
  }, []);

  /** Send the answer, refresh /auth/me, then hand over to the paywall — which
   * shows the plan choice after accepting, or the ended-access view after declining. */
  const answer = async (choice) => {
    setBusy(true); setError('');
    try {
      await api('/auth/golive/', { method: 'POST', body: { choice } });
      await reload();
      nav('/paywall');
    } catch (e) {
      setError(e.data?.detail || e.message);
      setBusy(false);
    }
  };

  const monthly = plans?.price_monthly_zar ?? 59;
  const yearly = plans?.price_yearly_zar ?? 590;

  return (
    <div className="fixed inset-0 z-[200] bg-ink/95 backdrop-blur-sm grid place-items-center px-4 overflow-y-auto"
         role="dialog" aria-modal="true" aria-labelledby="golive-title">
      <div className="card max-w-md w-full text-center !p-8 my-8">
        <img src="/logo.svg" alt="" className="w-14 h-14 mx-auto" />
        {step === 'ask' ? (
          <>
            <h1 id="golive-title" className="text-2xl font-bold mt-4">Vault is going live</h1>
            <p className="mt-3 text-slate-400">
              Thank you for helping me test Vault. Testing is over and Vault is now a paid app.
              Nothing has been charged to you.
            </p>
            <p className="mt-3 text-slate-300">
              To keep your budgets and carry on, choose Vault Pro
              at {fmtR(monthly)}/month{plans?.yearly_available ? ` or ${fmtR(yearly)}/year` : ''}.
              You can cancel at any time.
            </p>
            <button className="btn-primary w-full mt-6" disabled={busy} onClick={() => answer('accept')}>
              {busy ? 'One moment…' : 'Continue with Vault Pro'}
            </button>
            <button className="w-full mt-3 py-2 rounded-xl border border-edge text-slate-300 hover:border-slate-500 transition"
                    disabled={busy} onClick={() => setStep('confirm-decline')}>
              No thanks, I won't carry on
            </button>
          </>
        ) : (
          <>
            <h1 id="golive-title" className="text-2xl font-bold mt-4">Stop using Vault?</h1>
            <p className="mt-3 text-slate-400">
              Your access ends now and nothing is charged. Your data stays yours: you can
              still download it or delete your account from the Account page, and you can
              come back and subscribe later.
            </p>
            <button className="w-full mt-6 py-2 rounded-xl border border-red-400/60 text-red-300 hover:bg-red-400/10 transition"
                    disabled={busy} onClick={() => answer('decline')}>
              {busy ? 'One moment…' : 'Yes, stop using Vault'}
            </button>
            <button className="btn-primary w-full mt-3" disabled={busy} onClick={() => setStep('ask')}>
              Go back
            </button>
          </>
        )}
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        <button className="mt-4 text-sm text-slate-500 hover:text-slate-300" onClick={logout}>
          Sign out
        </button>
      </div>
    </div>
  );
}
